/**
 * A ⌘/Ctrl click or middle click on a link is the user asking the browser for
 * a new tab. Many of our click handlers call preventDefault() to run a custom
 * action instead of navigating (open a sheet, scroll to a section, …), which
 * also swallowed that request. The guard runs at window capture, ahead of every
 * React handler, and stops the event there so the browser's default is all
 * that runs.
 *
 * Middle clicks reach links as `auxclick` in current browsers, and as `click`
 * with button 1 in Safari before 18.2, so both events are watched.
 */

// the nearest of these decides: a button nested inside a card link is its own
// control, not the link
const CONTROL_SELECTOR = "a[href], button, [role='button']"

// links that are really buttons or in-page hooks have nothing to open in a tab
function isNavigableHref(href: string | null): boolean {
  if (!href || href === "#") return false
  return !/^\s*javascript:/i.test(href)
}

export function isNewTabLinkClick(event: MouseEvent): boolean {
  const wantsNewTab = event.metaKey || event.ctrlKey || event.button === 1
  if (!wantsNewTab || !(event.target instanceof Element)) return false
  const control = event.target.closest(CONTROL_SELECTOR)
  return (
    control instanceof HTMLAnchorElement &&
    isNavigableHref(control.getAttribute("href"))
  )
}

export function installNewTabClickGuard(target: Window): () => void {
  const listener = (event: MouseEvent) => {
    if (isNewTabLinkClick(event)) event.stopPropagation()
  }
  target.addEventListener("click", listener, true)
  target.addEventListener("auxclick", listener, true)
  return () => {
    target.removeEventListener("click", listener, true)
    target.removeEventListener("auxclick", listener, true)
  }
}
